import {
  SpotEntryConfig,
  SpotNominationDetail,
  SpotNominationPage,
  SpotPeoplePage,
  SpotPerson,
  SpotReceivedPage,
  type CreateSpotDraft,
  type SaveSpotDraft,
  type SubmitSpotDraft,
} from '@myrewards/contracts';
import { request } from './client';
const base = '/api/v1/spot';
const path = (id: string): string => `${base}/${encodeURIComponent(z.uuid().parse(id))}`;
export const getSpotConfig = (signal?: AbortSignal): Promise<SpotEntryConfig> =>
  request(base, { schema: SpotEntryConfig, ...(signal && { signal }) });
export const getSpotDetail = (id: string, signal?: AbortSignal): Promise<SpotNominationDetail> =>
  request(path(id), { schema: SpotNominationDetail, ...(signal && { signal }) });
export const getSpotPerson = (id: string, signal?: AbortSignal): Promise<SpotPerson> =>
  request(`${base}/people/${encodeURIComponent(id)}`, {
    schema: SpotPerson,
    ...(signal && { signal }),
  });
export const searchSpotPeople = (
  q: string,
  signal?: AbortSignal,
): Promise<{ items: SpotPerson[] }> =>
  request(`${base}/people?${new URLSearchParams({ q })}`, {
    schema: SpotPeoplePage,
    ...(signal && { signal }),
  });
/** Which page of a list, and how many rows on it (APP-135). */
export interface SpotPaging {
  page: number;
  size: number;
}
const pagePath = (
  view: string,
  { page, size }: SpotPaging,
  reviewState?: 'pending' | 'completed',
): string =>
  `${base}/${view}?${new URLSearchParams({ page: String(page), size: String(size), ...(reviewState ? { reviewState } : {}) })}`;
export const listSpotNominations = (
  view: 'mine' | 'review',
  paging: SpotPaging,
  signal?: AbortSignal,
  reviewState?: 'pending' | 'completed',
): Promise<SpotNominationPage> =>
  request(pagePath(view, paging, reviewState), {
    schema: SpotNominationPage,
    ...(signal && { signal }),
  });
export const listSpotReceived = (
  paging: SpotPaging,
  signal?: AbortSignal,
): Promise<SpotReceivedPage> =>
  request(pagePath('received', paging), { schema: SpotReceivedPage, ...(signal && { signal }) });
export const createSpotDraft = (body: CreateSpotDraft): Promise<SpotNominationDetail> =>
  request(base, { method: 'POST', schema: SpotNominationDetail, body });
export const saveSpotDraft = (id: string, body: SaveSpotDraft): Promise<SpotNominationDetail> =>
  request(path(id), { method: 'PUT', schema: SpotNominationDetail, body });
export const submitSpotDraft = (id: string, body: SubmitSpotDraft): Promise<SpotNominationDetail> =>
  request(`${path(id)}/submit`, { method: 'POST', schema: SpotNominationDetail, body });

// Purpose-specific projections: supervisors never fetch full nomination details.
import {
  EditSpotLetterTitle,
  SpotCommitteeHistory,
  SpotCommitteeQueuePage,
  PreviewSpotCommittee,
  SpotCommitteePreview,
  DecideSpotCommittee,
  DecideSpotSupervisor,
  AssignSpotSupervisor,
  RequestSpotFunding,
  DecideSpotFunding,
  SpotSupervisionPage,
  SpotSupervisionItem,
  SpotFundingPage,
  SpotFundingItem,
  AwardLetters,
  GenerateLetters,
  ReleaseLetters,
  RerouteSpotLetter,
  SaveSpotCitation,
  StatementDecision,
  StatementDecisionResult,
} from '@myrewards/contracts';
import { z } from 'zod';
const memberPath = (id: string, personId: string): string =>
  `${path(z.uuid().parse(id))}/nominees/${encodeURIComponent(z.uuid().parse(personId))}`;
export const previewSpotCommittee = (id: string, body: PreviewSpotCommittee) =>
  request(`${path(id)}/committee/preview`, {
    method: 'POST',
    body: PreviewSpotCommittee.parse(body),
    schema: SpotCommitteePreview,
  });
/** APP-241 (Q26): the committee saves a colleague's letter title, until it decides on them. */
export const editSpotLetterTitle = (id: string, personId: string, body: EditSpotLetterTitle) =>
  request(`${memberPath(id, personId)}/letter-title`, {
    method: 'PUT',
    body: EditSpotLetterTitle.parse(body),
    schema: SpotNominationDetail,
  });
/** APP-241 (change 8): each colleague's awards in the last 12 months, for the committee's review. */
export const getSpotHistory = (id: string, signal?: AbortSignal): Promise<SpotCommitteeHistory> =>
  request(`${path(id)}/history`, { schema: SpotCommitteeHistory, ...(signal && { signal }) });
/** APP-241 (change 6): the committee queue, filtered and sorted on the server, then paged. */
export const listSpotCommittee = (
  paging: SpotPaging,
  query: {
    stage: string;
    value: string;
    sent: string;
    sort: string;
    dir: 'asc' | 'desc';
  },
  signal?: AbortSignal,
): Promise<SpotCommitteeQueuePage> =>
  request(
    `${base}/committee?${new URLSearchParams({
      page: String(paging.page),
      size: String(paging.size),
      ...query,
    })}`,
    { schema: SpotCommitteeQueuePage, ...(signal && { signal }) },
  );
export const decideSpotCommittee = (id: string, body: DecideSpotCommittee) =>
  request(`${path(id)}/committee-decision`, {
    method: 'POST',
    body: DecideSpotCommittee.parse(body),
    schema: SpotNominationDetail,
  });
export const listSpotSupervision = (
  paging: SpotPaging,
  signal?: AbortSignal,
  reviewState?: 'pending' | 'completed',
) =>
  request(pagePath('supervision', paging, reviewState), {
    schema: SpotSupervisionPage,
    ...(signal && { signal }),
  });
export const getSpotSupervision = (id: string, personId: string, signal?: AbortSignal) =>
  request(`${memberPath(id, personId)}/supervision`, {
    schema: SpotSupervisionItem,
    ...(signal && { signal }),
  });
export const decideSpotSupervisor = (id: string, personId: string, body: DecideSpotSupervisor) =>
  request(`${memberPath(id, personId)}/supervisor-decision`, {
    method: 'POST',
    body: DecideSpotSupervisor.parse(body),
    schema: SpotSupervisionItem,
  });
/** APP-243 (Q26): the manager polishes the colleague's letter title until they approve. */
/** APP-243 (Q26): the manager's letter title edit, on their own decision, until they approve. */
export const editSupervisionLetterTitle = (
  id: string,
  personId: string,
  body: EditSpotLetterTitle,
) =>
  request(`${memberPath(id, personId)}/supervision/letter-title`, {
    method: 'PUT',
    body: EditSpotLetterTitle.parse(body),
    schema: SpotSupervisionItem,
  });
export const assignSpotSupervisor = (id: string, personId: string, body: AssignSpotSupervisor) =>
  request(`${memberPath(id, personId)}/supervisor-assignment`, {
    method: 'POST',
    body: AssignSpotSupervisor.parse(body),
    schema: z.object({ version: z.number().int().positive() }).strict(),
  });
export const requestSpotFunding = (id: string, personId: string, body: RequestSpotFunding) =>
  request(`${memberPath(id, personId)}/funding-request`, {
    method: 'POST',
    body: RequestSpotFunding.parse(body),
    schema: SpotFundingItem,
  });
export const listSpotFunding = (paging: SpotPaging, signal?: AbortSignal) =>
  request(pagePath('funding', paging), { schema: SpotFundingPage, ...(signal && { signal }) });
export const decideSpotFunding = (id: string, body: DecideSpotFunding) =>
  request(`${base}/funding/${encodeURIComponent(z.uuid().parse(id))}/decision`, {
    method: 'POST',
    body: DecideSpotFunding.parse(body),
    schema: SpotFundingItem,
  });
const lettersPath = (id: string): string =>
  `/api/v1/awards/${encodeURIComponent(z.uuid().parse(id))}/letters`;
export const getSpotLetters = (id: string, signal?: AbortSignal) =>
  request(lettersPath(id), { schema: AwardLetters, ...(signal && { signal }) });
export const generateSpotLetters = (id: string, body: GenerateLetters) =>
  request(`${lettersPath(id)}/generate`, {
    method: 'POST',
    body: GenerateLetters.parse(body),
    schema: AwardLetters,
  });
export const releaseSpotLetters = (id: string, body: ReleaseLetters) =>
  request(`${lettersPath(id)}/release`, {
    method: 'POST',
    body: ReleaseLetters.parse(body),
    schema: AwardLetters,
  });
export const rerouteSpotLetter = (id: string, lineId: string, body: RerouteSpotLetter) =>
  request(
    `/api/v1/spot-fulfillment/awards/${encodeURIComponent(z.uuid().parse(id))}/lines/${encodeURIComponent(z.uuid().parse(lineId))}/letter-holder`,
    { method: 'POST', body: RerouteSpotLetter.parse(body), schema: AwardLetters },
  );
export const spotLetterHref = (id: string, lineId: string): string =>
  `${lettersPath(id)}/${encodeURIComponent(z.uuid().parse(lineId))}/content`;
export const listSpotStatements = (signal?: AbortSignal) =>
  request('/api/v1/spot-fulfillment/statements', {
    schema: SpotStatementList,
    ...(signal && { signal }),
  });
export const decideSpotStatement = (lineId: string, body: StatementDecision) =>
  request(
    `/api/v1/spot-fulfillment/lines/${encodeURIComponent(z.uuid().parse(lineId))}/statement-decision`,
    { method: 'POST', body: StatementDecision.parse(body), schema: StatementDecisionResult },
  );

import {
  SpotFulfillmentItem,
  SpotFulfillmentPage,
  SpotAssignmentItem,
  SpotCandidatePage,
  SpotStatementList,
} from '@myrewards/contracts';
export const listSpotSupervisorCandidates = (
  id: string,
  personId: string,
  q: string,
  signal?: AbortSignal,
) =>
  request(`${memberPath(id, personId)}/supervisors?${new URLSearchParams({ q, limit: '20' })}`, {
    schema: SpotCandidatePage,
    ...(signal && { signal }),
  });
export const listSpotFulfillment = (paging: SpotPaging, signal?: AbortSignal) =>
  request(pagePath('fulfillment', paging), {
    schema: SpotFulfillmentPage,
    ...(signal && { signal }),
  });
export const getSpotFulfillment = (id: string, signal?: AbortSignal) =>
  request(`${base}/fulfillment/${encodeURIComponent(z.uuid().parse(id))}`, {
    schema: SpotFulfillmentItem,
    ...(signal && { signal }),
  });
export const listSpotHolderCandidates = (id: string, q: string, signal?: AbortSignal) =>
  request(
    `${base}/fulfillment/${encodeURIComponent(z.uuid().parse(id))}/holders?${new URLSearchParams({ q, limit: '20' })}`,
    { schema: SpotCandidatePage, ...(signal && { signal }) },
  );

export const getSpotFunding = (id: string, signal?: AbortSignal) =>
  request(`${base}/funding/${encodeURIComponent(z.uuid().parse(id))}`, {
    schema: SpotFundingItem,
    ...(signal && { signal }),
  });
export const getSpotAssignment = (id: string, personId: string, signal?: AbortSignal) =>
  request(`${memberPath(id, personId)}/assignment`, {
    schema: SpotAssignmentItem,
    ...(signal && { signal }),
  });

export const saveSpotCitation = (id: string, lineId: string, body: SaveSpotCitation) =>
  request(
    `/api/v1/spot-fulfillment/awards/${encodeURIComponent(z.uuid().parse(id))}/lines/${encodeURIComponent(z.uuid().parse(lineId))}/citation`,
    { method: 'POST', body: SaveSpotCitation.parse(body), schema: AwardLetters },
  );

import {
  CancelSpotNominee,
  SpotInProgress,
  SpotQueuePage,
  SpotQueueRow,
  type SpotQueueSort,
  type SpotQueueStageFilter,
  type SpotQueueWaiting,
} from '@myrewards/contracts';
/**
 * APP-226: All Spot awards. APP-233: filtered by stage (In progress unless asked) and who it waits
 * on, and sorted on the server over the whole list before it's paged.
 */
export const listSpotQueue = (
  paging: SpotPaging,
  query: {
    stage: SpotQueueStageFilter;
    waiting: SpotQueueWaiting | null;
    sort: SpotQueueSort;
    dir: 'asc' | 'desc';
  },
  signal?: AbortSignal,
): Promise<SpotQueuePage> =>
  request(
    `${base}/all?${new URLSearchParams({
      page: String(paging.page),
      size: String(paging.size),
      stage: query.stage,
      ...(query.waiting === null ? {} : { waiting: query.waiting }),
      sort: query.sort,
      dir: query.dir,
    })}`,
    { schema: SpotQueuePage, ...(signal && { signal }) },
  );
/** APP-226: cancel one nominee before the letter is released, with a reason. */
export const cancelSpotNominee = (id: string, personId: string, body: CancelSpotNominee) =>
  request(`${memberPath(id, personId)}/cancel`, {
    method: 'POST',
    body: CancelSpotNominee.parse(body),
    schema: SpotQueueRow,
  });
/**
 * APP-232: Home's Spot awards in progress, counts by stage, for whoever reads every row of All Spot
 * awards (the committee, the Stock Admin team, System Admins); 404 for anyone else.
 */
export const getSpotInProgress = (signal?: AbortSignal): Promise<SpotInProgress> =>
  request(`${base}/in-progress`, { schema: SpotInProgress, ...(signal && { signal }) });
